import { useState, useMemo, useEffect } from "react";
import {
  Service,
  ServiceVariant,
  Business,
  BookingService,
} from "@/types/booking";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/responsive-dialog";
import { Clock, Check } from "lucide-react";
import { formatDuration, formatPrice, getBusinessCurrency } from "@/lib/utils";

interface ServiceVariantDialogProps {
  service: Service;
  businessInfo: Business | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectVariant: (serviceId: string, variant: ServiceVariant | null) => void;
  allowMultiple?: boolean;
  selectedServices?: BookingService[]; // Add selected services to check what's already in cart
}

export default function ServiceVariantDialog({
  service,
  businessInfo,
  isOpen,
  onClose,
  onSelectVariant,
  allowMultiple = false,
  selectedServices = [],
}: ServiceVariantDialogProps) {
  const [selectedVariant, setSelectedVariant] = useState<
    ServiceVariant | "parent" | null
  >(null);
  const [selectedVariants, setSelectedVariants] = useState<ServiceVariant[]>(
    []
  );
  const [isParentSelected, setIsParentSelected] = useState(false);
  const currency = getBusinessCurrency(businessInfo);

  // Initialize state based on what's already selected
  useEffect(() => {
    if (isOpen && selectedServices.length > 0) {
      // Check if parent service is selected
      const parentSelected = selectedServices.some((s) => s.id === service.id);
      setIsParentSelected(parentSelected);

      // Check which variants are selected
      const selectedVars: ServiceVariant[] = [];
      selectedServices.forEach((selectedService) => {
        // Check if this is a variant of our service
        if (
          selectedService.id.startsWith(`${service.id}-`) &&
          selectedService.variantId
        ) {
          const variant = service.serviceVariants?.find(
            (v) => v.id === selectedService.variantId
          );
          if (variant) {
            selectedVars.push(variant);
          }
        }
      });
      setSelectedVariants(selectedVars);

      // For single selection mode, set the appropriate selection
      if (!allowMultiple) {
        if (parentSelected) {
          setSelectedVariant("parent");
        } else if (selectedVars.length > 0) {
          setSelectedVariant(selectedVars[0]);
        }
      }
    } else if (isOpen) {
      // Reset state when dialog opens with no selections
      setSelectedVariant(null);
      setSelectedVariants([]);
      setIsParentSelected(false);
    }
  }, [
    isOpen,
    selectedServices,
    service.id,
    service.serviceVariants,
    allowMultiple,
  ]);

  // Calculate price and duration ranges
  const priceRange = useMemo(() => {
    const allPrices = [
      service.price,
      ...(service.serviceVariants || []).map((v) => v.price),
    ];
    const minPrice = Math.min(...allPrices);
    const maxPrice = Math.max(...allPrices);
    return { min: minPrice, max: maxPrice };
  }, [service.price, service.serviceVariants]);

  const durationRange = useMemo(() => {
    const allDurations = [
      service.duration,
      ...(service.serviceVariants || []).map((v) => v.duration),
    ];
    const minDuration = Math.min(...allDurations);
    const maxDuration = Math.max(...allDurations);
    return { min: minDuration, max: maxDuration };
  }, [service.duration, service.serviceVariants]);

  const handleSelectVariant = (variant: ServiceVariant | "parent" | null) => {
    if (allowMultiple) {
      if (variant) {
        if (variant === "parent") {
          // Handle parent service selection in multiple mode
          setIsParentSelected((prev) => !prev);
        } else {
          setSelectedVariants((prev) =>
            prev.find((v) => v.id === variant.id)
              ? prev.filter((v) => v.id !== variant.id)
              : [...prev, variant]
          );
        }
      }
    } else {
      // For single selection, variant can be "parent", ServiceVariant, or null
      setSelectedVariant(variant);
    }
  };

  const handleConfirmSelection = () => {
    if (allowMultiple) {
      // For multiple selection, handle both additions and removals

      // First, remove all previously selected items for this service
      selectedServices.forEach((selectedService) => {
        if (
          selectedService.id === service.id ||
          selectedService.id.startsWith(`${service.id}-`)
        ) {
          onSelectVariant(selectedService.id, null); // This will remove it
        }
      });

      // Then add all currently selected items
      if (isParentSelected) {
        onSelectVariant(service.id, null); // Add parent service
      }
      selectedVariants.forEach((variant) => {
        const uniqueServiceId = `${service.id}-${variant.id}`;
        onSelectVariant(uniqueServiceId, variant);
      });
    } else {
      // For single selection, remove previous selection and add new one
      if (selectedVariant === "parent") {
        // Remove any existing selections for this service first
        selectedServices.forEach((selectedService) => {
          if (
            selectedService.id === service.id ||
            selectedService.id.startsWith(`${service.id}-`)
          ) {
            onSelectVariant(selectedService.id, null);
          }
        });
        onSelectVariant(service.id, null);
      } else if (selectedVariant) {
        // Remove any existing selections for this service first
        selectedServices.forEach((selectedService) => {
          if (
            selectedService.id === service.id ||
            selectedService.id.startsWith(`${service.id}-`)
          ) {
            onSelectVariant(selectedService.id, null);
          }
        });
        onSelectVariant(service.id, selectedVariant);
      } else {
        // Nothing selected, remove all existing selections for this service
        selectedServices.forEach((selectedService) => {
          if (
            selectedService.id === service.id ||
            selectedService.id.startsWith(`${service.id}-`)
          ) {
            onSelectVariant(selectedService.id, null);
          }
        });
      }
    }
    onClose();
    setSelectedVariant(null);
    setSelectedVariants([]);
    setIsParentSelected(false);
  };

  const handleClose = () => {
    onClose();
    setSelectedVariant(null);
    setSelectedVariants([]);
    setIsParentSelected(false);
  };

  // Create all options including the parent service
  const allOptions = [
    {
      id: "parent", // Use "parent" as the ID for consistency
      name: service.name,
      description: service.description,
      price: service.price,
      duration: service.duration,
      isParent: true,
    },
    ...(service.serviceVariants || []).map((variant) => ({
      id: variant.id,
      name: variant.name,
      description: variant.description,
      price: variant.price,
      duration: variant.duration,
      isParent: false,
    })),
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <div className="p-6 pb-0 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-grey-900">
              {service.name}
            </DialogTitle>
            <p className="text-grey-600 text-sm">
              {service.description ||
                "Select the specific service option you'd like to book"}
            </p>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 space-y-2">
          <div className="mb-4">
            <p className="text-sm font-medium text-grey-700 mb-3">
              Select an option *
            </p>
          </div>

          {allOptions.map((option) => {
            const isSelected = allowMultiple
              ? (option.isParent && isParentSelected) ||
                (!option.isParent &&
                  selectedVariants.some((v) => v.id === option.id))
              : (option.isParent && selectedVariant === "parent") ||
                (!option.isParent &&
                  selectedVariant &&
                  typeof selectedVariant === "object" &&
                  selectedVariant.id === option.id);

            return (
              <div
                key={option.id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  isSelected
                    ? "border-green-300 bg-green-50"
                    : "border-grey-100 hover:border-grey-150 hover:bg-grey-50"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectVariant(
                    option.isParent
                      ? "parent"
                      : service.serviceVariants?.find(
                          (v) => v.id === option.id
                        ) || null
                  );
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Radio Button */}
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? "border-green-300 bg-green-300"
                          : "border-grey-300"
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-grey-900">
                        {option.name}
                      </h3>
                      {option.description && (
                        <p className="text-grey-600 text-sm mt-1">
                          {option.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="text-right ml-4">
                    <div className="flex items-center justify-end gap-1.5 text-sm text-grey-600 mb-1">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">
                        {formatDuration(option.duration)}
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-green-300">
                      {formatPrice(option.price, currency)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex-shrink-0 p-6 pt-4 border-t border-grey-100 bg-white">
          {/* Price and Duration Range */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-grey-600">
              <div>
                <span className="font-medium">
                  from{" "}
                  {formatPrice(priceRange.min, currency)}
                </span>
                {priceRange.min !== priceRange.max && (
                  <span className="ml-1">
                    -{" "}
                    {formatPrice(priceRange.max, currency)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>
                  {formatDuration(durationRange.min)}
                  {durationRange.min !== durationRange.max &&
                    ` - ${formatDuration(durationRange.max)}`}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-grey-100 hover:border-grey-150"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSelection}
              className="flex-1 bg-green-300 text-white hover:bg-green-400"
              disabled={
                allowMultiple
                  ? selectedVariants.length === 0 && !isParentSelected
                  : false // Always allow selection in single mode since we have options
              }
            >
              {allowMultiple
                ? `Add ${
                    selectedVariants.length + (isParentSelected ? 1 : 0)
                  } to booking`
                : "Add to booking"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
